import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectActivities, selectActiveCourse } from '../redux/classroomSlice';
import { setActivityContent } from '../redux/contentSlice';
import { ProfessorService } from '../services/llmClient/ProfessorService';
import { ExaminerService } from '../services/llmClient/ExaminerService';
import { LibrarianService } from '../services/llmClient/LibrarianService';
import { useReady } from '../hooks/useReady';
import { selectProfile as selectStudentProfile } from '../redux/profileSlice';

const getServiceForLeader = (leader) => {
  switch (leader) {
    case 'Professor':
      return ProfessorService;
    case 'Examiner':
      return ExaminerService;
    case 'Librarian':
      return LibrarianService;
    default:
      return null;
  }
};

// Retry-safe wrapper with exponential backoff
const prefetchWithRetry = async (activity, fetchFn, retries = 3, delayMs = 1000) => {
  try {
    return await fetchFn(activity);
  } catch (error) {
    if (retries > 0 && error.message?.includes('rate limit')) {
      await new Promise((res) => setTimeout(res, delayMs));
      return prefetchWithRetry(activity, fetchFn, retries - 1, delayMs * 2);
    }
    throw error;
  }
};

export function useBackgroundPrefetcher(activeActivityId, minimumPrefetchCount = 3) {
  const dispatch = useDispatch();
  const activities = useSelector(selectActivities);
  const profile = useSelector(selectStudentProfile);
  const course = useSelector(selectActiveCourse);
  const isDataReady = useReady([profile, course]);

  const inProgressRef = useRef(false);
  const prefetchQueue = useRef([]);
  const fetchedContentRef = useRef({});
  const completedActivitiesRef = useRef(new Set());
  const [isNextReady, setIsNextReady] = useState(false);

  const prefetchStartedRef = useRef(false);

  const prefetchActivityContent = useCallback(
    async (activity) => {
      if (!activity || completedActivitiesRef.current.has(activity.id)) return;

      const Service = getServiceForLeader(activity.leader);
      if (!Service) {
        completedActivitiesRef.current.add(activity.id);
        return;
      }

      const content = await Service.generateContentForActivity(activity, profile, course);
      if (content) {
        fetchedContentRef.current[activity.id] = content;
        dispatch(setActivityContent({ activityId: activity.id, content }));
        completedActivitiesRef.current.add(activity.id);
      }
    },
    [dispatch, profile, course]
  );

  const runPrefetcher = useCallback(async () => {
    if (inProgressRef.current) return;
    inProgressRef.current = true;

    let count = 0;
    while (prefetchQueue.current.length > 0) {
      const nextActivity = prefetchQueue.current.shift();

      // Avoid double-prefetch just in case
      if (completedActivitiesRef.current.has(nextActivity.id)) continue;

      try {
        await prefetchWithRetry(nextActivity, prefetchActivityContent);
        count++;
        if (count >= minimumPrefetchCount && !isNextReady) {
          setIsNextReady(true);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`⚠️ Prefetch failed: ${nextActivity?.title || 'Unknown activity'}`);
      }

      await new Promise((resolve) => setTimeout(resolve, 2000)); // delay per request
    }

    inProgressRef.current = false;
    setIsNextReady(true);
  }, [prefetchActivityContent, minimumPrefetchCount, isNextReady]);

  useEffect(() => {
    if (!isDataReady || inProgressRef.current || prefetchStartedRef.current) return;

    // Refill queue with only unfinished activities
    prefetchQueue.current = activities.filter((a) => !completedActivitiesRef.current.has(a.id));

    setIsNextReady(false);
    runPrefetcher();

    return () => {
      inProgressRef.current = false;
      prefetchQueue.current = [];
      // Preserve fetched and completed state across rerenders
    };
  }, [activeActivityId, isDataReady, activities, runPrefetcher]);

  return {
    prefetchActivityContent,
  };
}
